import { useCallback } from "react";
import { useTranslation } from "../context/TranslationContext";
import { DefaultTranslations } from "../utils/seedDefaultTranslation";
import {
  KeyOf,
  SectionOf,
  TabId,
} from "../services/api/types/portalConfiguration/translations";

type InterpolationParams = Record<string, string | number>;

// Positional, not name-matched: machine translation of a seeded string can (and does)
// translate the word inside "{token}" along with the rest of the sentence, so the
// braces survive but the name inside no longer matches our params key. Substituting
// by occurrence order instead of by name survives that, as long as call sites pass
// params in the same order the placeholders appear in the English template.
const interpolate = (raw: string, params?: InterpolationParams): string => {
  if (!params) return raw;
  const values = Object.values(params);
  let i = 0;
  return raw.replace(/\{[^{}]*\}/g, (match) =>
    i < values.length ? String(values[i++]) : match,
  );
};

export const useTranslate = <T extends TabId>(tabId: T) => {
  const { translations } = useTranslation();

  return useCallback(
    <S extends SectionOf<T>>(
      section: S,
      key: KeyOf<T, S>,
      params?: InterpolationParams,
    ): string => {
      const liveValue = (translations.translations[tabId] as any)?.[section]?.[
        key
      ];
      const fallbackValue = (DefaultTranslations as any)[tabId]?.[section]?.[
        key
      ];
      const raw: string =
        typeof liveValue === "string" && liveValue.trim() !== ""
          ? liveValue
          : fallbackValue ?? key;

      return interpolate(raw, params);
    },
    [translations.translations, tabId],
  );
};
